import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customersApi } from '@/lib/api';
import { ShieldCheck, Trash2, User, UserPlus } from 'lucide-react';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Kontakt,
  Skeleton,
  StatusPill,
  formatDatum,
} from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { ActivityTimeline } from '@/features/interactions/components/ActivityTimeline';
import { InteractionSheet } from '@/features/interactions/components/InteractionSheet';
import { ConvertLeadModal } from './ConvertLeadModal';
import { StatusSelect } from '@/features/customers/components/StatusSelect';
import { TagToggleButton } from '@/features/customers/components/TagToggleButton';
import type { CustomerStatus } from '@/types';

const sourceLabels: Record<string, string> = {
  website: 'Website',
  empfehlung: 'Empfehlung',
  messe: 'Messe',
  kaltakquise: 'Kaltakquise',
  partner: 'Partner',
  bestandskunde: 'Bestandskunde',
  social_media: 'Social Media',
  sonstige: 'Sonstige',
};

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showConvert, setShowConvert] = useState(false);
  const [showInteractionForm, setShowInteractionForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: lead, isPending } = useQuery({
    queryKey: ['leads', id],
    queryFn: () => customersApi.get(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => customersApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      navigate('/leads');
    },
  });

  if (isPending) {
    return (
      <div className="space-y-6 pt-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[300px] w-full rounded-lg" />
      </div>
    );
  }

  if (!lead) {
    return (
      <>
        <CrmPageHeader title="Lead" zurueck={{ to: '/leads', label: 'Zur Leadliste' }} />
        <EmptyState
          icon={UserPlus}
          title="Lead nicht gefunden"
          action={<Button onClick={() => navigate('/leads')}>Zur Leadliste</Button>}
        />
      </>
    );
  }

  const daysOld = Math.floor((Date.now() - new Date(lead.created_at).getTime()) / (1000 * 60 * 60 * 24));

  return (
    <div>
      <CrmPageHeader
        size="page"
        zurueck={{ to: '/leads', label: 'Zur Leadliste' }}
        title={lead.company_name}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            <StatusPill tone="info">Lead</StatusPill>
            <span className="t-mono">{lead.customer_number}</span>
            <span>
              Angelegt am {formatDatum(lead.created_at)} ({daysOld} Tage)
            </span>
            {lead.acquisition_source && <span>Quelle: {sourceLabels[lead.acquisition_source] || lead.acquisition_source}</span>}
          </span>
        }
        mehrAktionen={[{ label: 'Lead löschen', icon: Trash2, tone: 'danger', onSelect: () => setShowDeleteConfirm(true) }]}
        actions={
          <Button variant="primary" icon={ShieldCheck} onClick={() => setShowConvert(true)}>
            Als Kunde anlegen
          </Button>
        }
      >
        <div className="flex flex-wrap items-start gap-3 pb-2">
          <StatusSelect
            customerId={lead.id}
            currentStatus={lead.status as CustomerStatus}
            currentLostReason={lead.lost_reason}
            currentFollowUpAt={lead.follow_up_at}
          />
          <TagToggleButton customerId={lead.id} currentTags={lead.tags ?? []} />
        </div>
      </CrmPageHeader>

      <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Notes */}
          {lead.notes && (
            <Card title="Notizen">
              <p className="whitespace-pre-wrap">{lead.notes}</p>
            </Card>
          )}

          {/* Activity Timeline */}
          <Card title="Aktivitäten">
            <ActivityTimeline entityType="customer" entityId={id!} onAddInteraction={() => setShowInteractionForm(true)} />
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Info */}
          <Card title="Kontakt">
            <div className="space-y-3">
              <Kontakt email={lead.email} telefon={lead.phone} />
              {lead.primary_contact_name && (
                <div className="flex items-center gap-2">
                  <User size={16} strokeWidth={1.75} className="text-muted-foreground" aria-hidden />
                  {lead.primary_contact_id ? (
                    <Link to={`/contacts/${lead.primary_contact_id}`} className="inline-flex min-h-11 items-center text-link hover:underline md:min-h-0">
                      {lead.primary_contact_name}
                    </Link>
                  ) : (
                    <span>{lead.primary_contact_name}</span>
                  )}
                </div>
              )}
              {!lead.email && !lead.phone && !lead.primary_contact_name && (
                <p className="t-small text-muted-foreground">Keine Kontaktdaten</p>
              )}
            </div>
          </Card>

          {/* Assignment */}
          <Card title="Zuständig">
            <p>{lead.assigned_user_name || 'Nicht zugewiesen'}</p>
          </Card>
        </div>
      </div>

      {/* Convert Modal */}
      <ConvertLeadModal
        isOpen={showConvert}
        onClose={() => setShowConvert(false)}
        leadId={id!}
        leadName={lead.company_name}
        leadNummer={lead.customer_number}
        onConverted={(customerId) => navigate(`/customers/${customerId}`)}
      />

      <InteractionSheet
        open={showInteractionForm}
        onOpenChange={setShowInteractionForm}
        title="Interaktion anlegen"
        description={`Zu ${lead.company_name}`}
        customerId={id}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Lead löschen?"
        description={`„${lead.company_name}“ wird endgültig gelöscht.`}
        confirmLabel="Lead löschen"
        tone="danger"
        onConfirm={() => deleteMutation.mutateAsync()}
      />
    </div>
  );
}
